/* eslint-disable no-eq-null */
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Avatar } from "@octopusdeploy/design-system-components";
import type { ResourceCollection, UserResource, Repository, LicenseUsageResource, LicenseStatusResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import type { ReactNode } from "react";
import { useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { PageContent } from "~/components/PageContent/PageContent";
import List from "~/components/PagingList";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { UpsellDialogWithLicenseUsage } from "~/components/UpsellDialog/UpsellDialogWithLicenseUsage";
import { EntitlementLimitPopUpEvent } from "~/components/UpsellDialog/upsellDialogAnalytics";
import { getLicenseLimit } from "~/hooks/useLicenseLimit";
import ListTitle from "~/primitiveComponents/dataDisplay/ListTitle/ListTitle";
import { InviteOctoIdUserDialogLink } from "./InviteOctoIdUserDialogLink";
import { isOctopusIdEnabled } from "./identityProviders";
import styles from "./style.module.less";
interface UsersPageState extends DataBaseComponentState {
    usersResponse?: ResourceCollection<UserResource>;
    isOctoIdEnabled?: boolean;
}
interface UsersPageProps {
    isUserLimitReached: boolean | null;
    licensedLimit: number;
    isFreeTierEnabled: boolean;
    openUserLimitDialog: () => void;
    licenseSerialNumber?: string;
    loaderData: UsersPageLoaderData;
}
export interface UsersPageLoaderData {
    usersResponse: ResourceCollection<UserResource>;
    isOctoIdEnabled: boolean;
    licenseUsage: LicenseUsageResource | null;
    licenseStatus: LicenseStatusResource | null;
}
export const usersPageLoader = async (repository: Repository, isFreeTierEnabled: boolean): Promise<UsersPageLoaderData> => {
    // Load license data if free tier is enabled
    const licenseUsageAsync = isFreeTierEnabled ? repository.Licenses.getCurrentUsage() : Promise.resolve(null);
    const licenseStatusAsync = isFreeTierEnabled ? repository.Licenses.getCurrentStatus() : Promise.resolve(null);
    const usersResponseAsync = repository.Users.list();
    const isOctoIdEnabledAsync = isOctopusIdEnabled();
    return {
        usersResponse: await usersResponseAsync,
        isOctoIdEnabled: await isOctoIdEnabledAsync,
        licenseUsage: await licenseUsageAsync,
        licenseStatus: await licenseStatusAsync,
    };
};
class UserList extends List<UserResource> {
}
class UsersPageClass extends DataBaseComponent<UsersPageProps, UsersPageState> {
    constructor(props: UsersPageProps) {
        super(props);
        this.state = {
            usersResponse: props.loaderData.usersResponse,
            isOctoIdEnabled: props.loaderData.isOctoIdEnabled,
        };
    }
    render() {
        const [primaryPageAction, pageActions] = this.addNewUserPageActions();
        return (<PageContent header={{ title: "Users", primaryAction: primaryPageAction, pageActions }} busy={this.state.busy} errors={this.errors}>
                {this.state.usersResponse && (<UserList initialData={this.state.usersResponse} onRow={(item: UserResource) => this.buildUserRow(item)} onRowRedirectUrl={(item: UserResource) => links.userPage.generateUrl({ userId: item.Id })} onFilter={this.filter} filterSearchEnabled={true} apiSearchParams={["filter"]} filterHintText="Filter by user..."/>)}
            </PageContent>);
    }
    private addNewUserPageActions(): [
        PrimaryPageAction | undefined,
        PageAction[]
    ] {
        const isOctoIdEnabled = this.state.isOctoIdEnabled;
        if (isOctoIdEnabled === undefined) {
            return [undefined, []];
        }
        const upsellDialogPageAction = (label: string): PrimaryPageAction => ({
            type: "button",
            hasPermissions: isAllowed({ permission: Permission.UserEdit }),
            label,
            onClick: () => this.props.openUserLimitDialog?.(),
        });
        const createServiceAccountUpsellAction: PageAction = {
            type: "button",
            hasPermissions: isAllowed({ permission: Permission.UserEdit }),
            label: "Create Service Account",
            onClick: () => this.props.openUserLimitDialog?.(),
            buttonType: "secondary",
        };
        const upsellActions: [
            PrimaryPageAction,
            PageAction[]
        ] = isOctoIdEnabled ? [upsellDialogPageAction("Add Users"), [createServiceAccountUpsellAction]] : [upsellDialogPageAction("Invite User"), []];
        const addUserPrimaryPageAction: PrimaryPageAction = {
            type: "navigate",
            hasPermissions: isAllowed({ permission: Permission.UserEdit }),
            label: "Add Users",
            path: links.createUserPage.generateUrl(),
        };
        const createServiceAccountPageAction: PageAction = {
            type: "navigate",
            buttonType: "secondary",
            hasPermissions: isAllowed({ permission: Permission.UserEdit }),
            label: "Create Service Account",
            path: links.createUserPage.generateUrl(),
        };
        const inviteUserPrimaryPageAction: PrimaryPageAction = {
            type: "custom",
            hasPermissions: isAllowed({ permission: Permission.UserEdit }),
            key: "Invite User",
            content: <InviteOctoIdUserDialogLink label="Invite User" serialNumber={this.props.licenseSerialNumber ?? ""}/>,
        };
        if (this.props.isUserLimitReached && this.props.isFreeTierEnabled)
            return upsellActions;
        return isOctoIdEnabled ? [inviteUserPrimaryPageAction, [createServiceAccountPageAction]] : [addUserPrimaryPageAction, []];
    }
    private buildUserRow(user: UserResource): ReactNode {
        const usernameForAvatarText = user && user.DisplayName ? user.DisplayName : "";
        return (<div className={styles.row}>
                <div className={styles.avatar}>
                    <Avatar type="user" size="medium" isServiceUser={user && user.IsService} src={user.Links?.Avatar} alt={usernameForAvatarText} text={usernameForAvatarText}/>
                </div>
                <div>
                    <ListTitle>{user.DisplayName}</ListTitle>
                    <div>{user.EmailAddress || user.Username}</div>
                </div>
            </div>);
    }
    private filter(filter: string, resource: UserResource) {
        return (!filter ||
            filter.length === 0 ||
            !resource ||
            resource.Username.toLowerCase().includes(filter.toLowerCase()) ||
            (resource.EmailAddress && resource.EmailAddress.toLowerCase().includes(filter.toLowerCase())) ||
            resource.DisplayName.toLowerCase().includes(filter.toLowerCase()));
    }
    static displayName = "UsersPageClass";
}
interface UsersPageComponentProps {
    loaderData: UsersPageLoaderData;
}
export function UsersPage({ loaderData }: UsersPageComponentProps) {
    const { isLimitReached: isUserLimitReached, licensedLimit } = getLicenseLimit(loaderData.licenseUsage, "users");
    const { isOpen: isUserLimitDialogOpen, openDialog: openUserLimitDialog, closeDialog: closeUserLimitDialog } = useDialogTrigger();
    const isFreeTierEnabled = isFeatureToggleEnabled("free-tier");
    const simpleDispatchAction = useAnalyticSimpleActionDispatch();
    function openUserLimitDialogAndSendAnalytics() {
        openUserLimitDialog();
        simpleDispatchAction(EntitlementLimitPopUpEvent);
    }
    return (<>
            <UsersPageClass isUserLimitReached={isUserLimitReached} licensedLimit={licensedLimit} isFreeTierEnabled={isFreeTierEnabled} openUserLimitDialog={openUserLimitDialogAndSendAnalytics} licenseSerialNumber={loaderData.licenseStatus?.SerialNumber} loaderData={loaderData}/>
            <UpsellDialogWithLicenseUsage title="Ready to grow your team?" content={<>
                        You've reached your <strong>{licensedLimit}-user</strong> limit. Upgrade to add unlimited users, or remove users who no longer need access.
                    </>} open={isUserLimitDialogOpen} onClose={closeUserLimitDialog} licenseSerialNumber={loaderData.licenseStatus?.SerialNumber} licenseUsage={loaderData.licenseUsage}/>
        </>);
}
